import { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Pause, CheckCircle2, Circle, ChevronDown, ChevronUp, 
  Timer, Dumbbell, X, Plus, Minus, Trophy, SkipForward, Trash2,
  Sparkles, History, ArrowLeft, Info, AlertTriangle, MessageCircle, StickyNote, Loader2, Calculator, MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { WorkoutExercise, WorkoutSession as WorkoutSessionType } from "@/types/workout";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ExerciseGifModal } from "@/components/ExerciseGifModal";
import { OneRMCalculator } from "@/components/OneRMCalculator";
import { findMatchingPR, isCommonPRExercise, getDisplayNameForCommonExercise, getEstimatedWeight, hasEstimatedWeights } from "@/utils/exerciseMatching";

const WORKOUT_SESSION_STORAGE_KEY = 'active_workout_session';

interface WorkoutSessionProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void; // Called when user cancels/dismisses without completing
  planText?: string;
  onComplete: (session: WorkoutSessionType) => void | Promise<void>;
  existingPRs?: { lift: string; weight: number }[];
  onNewPR?: (lift: string, weight: number) => void;
  weightUnit?: 'lbs' | 'kg';
}

// Previous workout hints type
interface ExerciseHistory {
  exerciseName: string;
  lastWeight: number;
  lastReps: number;
  recommendedWeight: number;
  isEstimate?: boolean; // True if this is an AI estimate vs PR-based
  isFromPreviousWorkout?: boolean; // True if from previous workout history
}

// User profile for weight estimates
interface UserProfileForEstimates {
  sex?: string;
  experience?: string;
  weight?: number;
}

interface WorkoutDay {
  name: string;
  exercises: WorkoutExercise[];
}

function parseWorkoutPlanByDay(text: string): WorkoutDay[] {
  if (!text) return [];
  
  console.log('[WorkoutParser] Parsing workout plan, length:', text.length);
  console.log('[WorkoutParser] First 500 chars:', text.substring(0, 500));
  
  const days: WorkoutDay[] = [];
  const lines = text.split('\n');
  
  // Patterns for day headers - expanded to catch more formats
  const dayHeaderPatterns = [
    // Numbered day format: "1. Day 1: Upper Body" or "1  Day 1: Upper Body"
    /^\d+\.?\s+Day\s*\d+[:\s\-–]+(.+)/i,
    // Markdown headers: "## Day 1: Upper Body" or "### Day 2 - Lower Body"
    /^#{1,3}\s*\*?\*?(?:Day\s*\d+|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)[:\s\-–]*([^*\n]*)\*?\*?/i,
    // Bold format: "**Day 1: Upper Body**"
    /^\*\*(?:Day\s*\d+|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)[:\s\-–]*([^*]*)\*\*/i,
    // Plain format: "Day 1: Upper Body" or "Day 1 - Upper Body"
    /^(?:Day\s*\d+|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)[:\s\-–]+(.+)/i,
    // Loose match: just "Day X" at start of line
    /^Day\s+\d+\b/i,
  ];
  
  // Pattern 1: Markdown table rows like "| Bench Press | 4 x 8 | 135 lbs |" or "| Push-ups | 4 x AMRAP |"
  const tableRowPattern = /^\|?\s*([^|]+?)\s*\|\s*(\d+)\s*[x×]\s*([^|]+?)\s*\|/i;
  
  // Pattern 2: List format like "- Bench Press: 4 sets x 10 reps" or "• Bench Press - 4x10" or "4 x AMRAP"
  const listPattern = /^[\*\-•]?\s*\*?\*?([\w\s\-\/\(\)]+)\*?\*?\s*[:\-–]?\s*(\d+)\s*(?:sets?\s*(?:of|x|×)\s*)?(\d+(?:-\d+)?|AMRAP|max)\s*(?:reps?)?/i;
  
  // Pattern 3: Simple format like "Bench Press 4x10" or "Push-ups 4 x AMRAP"
  const simplePattern = /^([A-Za-z][A-Za-z\s\-\/\(\)]+?)\s+(\d+)\s*[x×]\s*(\d+(?:-\d+)?|AMRAP|max)/i;
  
  // Pattern 4: Circled number format like "① Barbell Bench Press" (used in some AI responses)
  const circledPattern = /^[①②③④⑤⑥⑦⑧⑨⑩]\s*(.+)/;
  
  // Common exercise names to validate extracted names
  const exerciseKeywords = [
    'press', 'squat', 'deadlift', 'curl', 'row', 'pull', 'push', 'fly', 'raise',
    'extension', 'crunch', 'plank', 'lunge', 'dip', 'shrug', 'pulldown', 'kickback',
    'thrust', 'bridge', 'step', 'jump', 'burpee', 'clean', 'snatch', 'jerk',
    'warm', 'stretch', 'cardio', 'run', 'walk', 'bike', 'machine', 'cable',
    'dumbbell', 'barbell', 'kettlebell', 'band', 'bodyweight', 'lat', 'pec',
    'tricep', 'bicep', 'shoulder', 'chest', 'back', 'leg', 'glute', 'ab', 'core'
  ];
  
  const isValidExerciseName = (name: string): boolean => {
    if (!name || name.length < 3 || name.length > 60) return false;
    const lower = name.toLowerCase();
    if (lower.includes('exercise') && (lower.includes('sets') || lower.includes('reps'))) return false;
    if (lower === 'sets' || lower === 'reps' || lower === 'weight' || lower === 'notes') return false;
    if (lower.startsWith('---') || lower.startsWith('===')) return false;
    return exerciseKeywords.some(kw => lower.includes(kw)) || (name.length >= 4 && /^[A-Za-z]/.test(name));
  };
  
  let currentDay: WorkoutDay | null = null;
  let exerciseIndex = 0;
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;
    
    // Check for day header
    let dayMatch: RegExpExecArray | null = null;
    for (const pattern of dayHeaderPatterns) {
      dayMatch = pattern.exec(trimmedLine);
      if (dayMatch) {
        console.log('[WorkoutParser] Found day header:', trimmedLine);
        break;
      }
    }
    
    if (dayMatch) {
      // Save previous day if it has exercises
      if (currentDay && currentDay.exercises.length > 0) {
        days.push(currentDay);
      }
      
      // Extract day name from the matched line
      const fullMatch = trimmedLine.replace(/^#+\s*/, '').replace(/^\d+\.?\s*/, '').replace(/\*+/g, '').trim();
      currentDay = {
        name: fullMatch || `Day ${days.length + 1}`,
        exercises: [],
      };
      continue;
    }
    
    // Parse exercise from line
    let match: RegExpExecArray | null = null;
    let name = '';
    let sets = 0;
    let reps = '';
    
    // Try circled number pattern first (used in some AI responses)
    const circledMatch = circledPattern.exec(trimmedLine);
    if (circledMatch) {
      // This line has exercise info, try to extract sets/reps from it
      const exerciseText = circledMatch[1];
      const setsRepsMatch = /(\d+)\s*[x×]\s*(\d+(?:-\d+)?|AMRAP|max)/i.exec(exerciseText);
      if (setsRepsMatch) {
        // Extract name before the sets/reps
        const nameMatch = exerciseText.substring(0, exerciseText.indexOf(setsRepsMatch[0])).trim();
        if (nameMatch && isValidExerciseName(nameMatch)) {
          name = nameMatch;
          sets = parseInt(setsRepsMatch[1]);
          reps = setsRepsMatch[2];
        }
      }
    }
    
    // Try table row pattern
    if (!name) {
      match = tableRowPattern.exec(trimmedLine);
      if (match) {
        name = match[1].trim().replace(/\*+/g, '').replace(/^\|/, '').trim();
        sets = parseInt(match[2]);
        // Extract reps - could be "8-10", "AMRAP", or "As Many Reps As Possible (AMRAP)"
        const repsRaw = match[3].trim();
        reps = /amrap|as many/i.test(repsRaw) ? 'AMRAP' : repsRaw;
      }
    }
    
    // Try list pattern
    if (!name || !isValidExerciseName(name)) {
      match = listPattern.exec(trimmedLine);
      if (match) {
        name = match[1].trim().replace(/\*+/g, '');
        sets = parseInt(match[2]);
        reps = match[3];
      }
    }
    
    // Try simple pattern
    if (!name || !isValidExerciseName(name)) {
      match = simplePattern.exec(trimmedLine);
      if (match) {
        name = match[1].trim();
        sets = parseInt(match[2]);
        reps = match[3];
      }
    }
    
    if (name && sets && reps && isValidExerciseName(name)) {
      // If no day started yet, create a default one
      if (!currentDay) {
        currentDay = { name: 'Today\'s Workout', exercises: [] };
      }
      
      // Avoid duplicates within the same day
      const exists = currentDay.exercises.some(e => e.name.toLowerCase() === name.toLowerCase());
      if (!exists) {
        console.log('[WorkoutParser] Found exercise:', name, sets, 'x', reps);
        // Handle AMRAP - default to 10 reps for set tracking
        const parsedReps = /\d+/.test(reps) ? parseInt(reps.split('-')[0]) : 10;
        currentDay.exercises.push({
          id: `ex-${exerciseIndex++}`,
          name,
          sets,
          reps,
          completed: false,
          skipped: false,
          actualSets: Array.from({ length: sets }, (_, i) => ({
            setNumber: i + 1,
            reps: parsedReps,
            weight: 0,
            completed: false,
          })),
        });
      }
    }
  }
  
  // Don't forget the last day
  if (currentDay && currentDay.exercises.length > 0) {
    days.push(currentDay);
  }
  
  console.log('[WorkoutParser] Parsed', days.length, 'workout days:', days.map(d => `${d.name} (${d.exercises.length} exercises)`));
  
  return days;
}

function getDefaultWorkout(): WorkoutExercise[] {
  return [
    { id: 'ex-1', name: 'Warm-up', sets: 1, reps: '5 min', completed: false, skipped: false, actualSets: [{ setNumber: 1, reps: 1, weight: 0, completed: false }] },
    { id: 'ex-2', name: 'Bench Press', sets: 4, reps: '8-10', completed: false, skipped: false, actualSets: Array.from({ length: 4 }, (_, i) => ({ setNumber: i + 1, reps: 10, weight: 135, completed: false })) },
    { id: 'ex-3', name: 'Rows', sets: 4, reps: '8-10', completed: false, skipped: false, actualSets: Array.from({ length: 4 }, (_, i) => ({ setNumber: i + 1, reps: 10, weight: 95, completed: false })) },
    { id: 'ex-4', name: 'Overhead Press', sets: 3, reps: '8-12', completed: false, skipped: false, actualSets: Array.from({ length: 3 }, (_, i) => ({ setNumber: i + 1, reps: 10, weight: 65, completed: false })) },
  ];
}

export function WorkoutSession({ 
  isOpen, 
  onClose, 
  onCancel,
  planText, 
  onComplete,
  existingPRs = [],
  onNewPR,
  weightUnit = 'lbs'
}: WorkoutSessionProps) {
  const [workoutDays, setWorkoutDays] = useState<WorkoutDay[]>([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  const [exercises, setExercises] = useState<WorkoutExercise[]>([]);
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  // Use a persisted start timestamp so timer survives app backgrounding
  const [workoutStartTime, setWorkoutStartTime] = useState<number>(() => Date.now());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [restTimer, setRestTimer] = useState<number | null>(null);
  const [isRestActive, setIsRestActive] = useState(false);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseSets, setNewExerciseSets] = useState(3);
  const [newExerciseReps, setNewExerciseReps] = useState(10);
  const [exerciseHistory, setExerciseHistory] = useState<Map<string, ExerciseHistory>>(new Map());
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [userProfileForEstimates, setUserProfileForEstimates] = useState<UserProfileForEstimates>({});
  const [selectedExerciseForGif, setSelectedExerciseForGif] = useState<string | null>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  // Persist workout session to localStorage - save start timestamp for background persistence
  useEffect(() => {
    if (isOpen && exercises.length > 0 && selectedDayIndex !== null) {
      const sessionData = {
        exercises,
        selectedDayIndex,
        workoutDays,
        workoutStartTime, // Save the start timestamp so timer survives app backgrounding
      };
      localStorage.setItem(WORKOUT_SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    }
  }, [isOpen, exercises, selectedDayIndex, workoutDays, workoutStartTime]);

  // Restore workout session from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      const savedSession = localStorage.getItem(WORKOUT_SESSION_STORAGE_KEY);
      if (savedSession) {
        try {
          const parsed = JSON.parse(savedSession);
          if (parsed.exercises?.length > 0) {
            setExercises(parsed.exercises);
            setSelectedDayIndex(parsed.selectedDayIndex);
            setWorkoutDays(parsed.workoutDays || []);
            // Restore the start timestamp - elapsed time is calculated from this
            if (typeof parsed.workoutStartTime === 'number') {
              setWorkoutStartTime(parsed.workoutStartTime);
              // Calculate current elapsed time from the saved start time
              const elapsedNow = Math.floor((Date.now() - parsed.workoutStartTime) / 1000);
              setElapsedSeconds(elapsedNow);
            }
          }
        } catch (e) {
          console.error('Failed to restore workout session:', e);
        }
      }
    }
  }, [isOpen]);

  // Clear storage when workout is completed or explicitly cancelled
  const clearWorkoutSession = useCallback(() => {
    localStorage.removeItem(WORKOUT_SESSION_STORAGE_KEY);
    setSelectedDayIndex(null);
    setExercises([]);
    setWorkoutDays([]);
    setExpandedExercise(null);
    setElapsedSeconds(0);
    setWorkoutStartTime(Date.now()); // Reset start time for next workout
    setRestTimer(null);
    setIsRestActive(false);
  }, []);

  const handleCancelWorkout = () => {
    setShowCancelConfirmation(true);
  };

  const confirmCancelWorkout = () => {
    clearWorkoutSession();
    setShowCancelConfirmation(false);
    onCancel?.();
    onClose();
    toast("Workout cancelled");
  };

  // Fetch AI recommendations and previous workout data
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!isOpen || exercises.length === 0) return;
      
      setLoadingRecommendations(true);
      console.log('[AIWeights] Starting recommendation fetch...');
      
      try {
        // Get user's PRs and previous workout data for recommendations
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('[AIWeights] No user found for recommendations');
          return;
        }

        // Fetch profile and previous workout sessions in parallel
        const [profileResult, workoutHistoryResult] = await Promise.all([
          supabase
            .from('profiles')
            .select('prs, sex, experience, weight')
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('workout_sessions')
            .select('exercise_data, completed_at')
            .eq('user_id', user.id)
            .order('completed_at', { ascending: false })
            .limit(10) // Get last 10 workout sessions
        ]);

        if (profileResult.error) {
          console.error('[AIWeights] Error fetching profile:', profileResult.error);
          return;
        }

        const profile = profileResult.data;

        // Store user profile for estimates
        const userProfile: UserProfileForEstimates = {
          sex: profile?.sex || undefined,
          experience: profile?.experience || undefined,
          weight: profile?.weight || undefined,
        };
        setUserProfileForEstimates(userProfile);
        console.log('[AIWeights] User profile for estimates:', userProfile);

        // Parse PRs with type safety
        const rawPrs = Array.isArray(profile?.prs) ? profile.prs : [];
        const typedPrs: { lift: string; weight: number }[] = rawPrs
          .filter((pr): pr is { lift?: string; weight?: number } => 
            typeof pr === 'object' && pr !== null && !Array.isArray(pr)
          )
          .filter((pr): pr is { lift: string; weight: number } => 
            typeof pr.lift === 'string' && typeof pr.weight === 'number'
          );
        
        console.log('[AIWeights] User PRs:', typedPrs);
        console.log('[AIWeights] Exercises to match:', exercises.map(e => e.name));
        
        // Build a map of previous workout weights from workout history
        const previousWorkoutWeights = new Map<string, { weight: number; reps: number }>();
        
        if (workoutHistoryResult.data) {
          for (const session of workoutHistoryResult.data) {
            const exerciseData = session.exercise_data as { name: string; sets: { reps: number; weight: number }[]; completed: boolean }[] | null;
            if (!Array.isArray(exerciseData)) continue;
            
            for (const ex of exerciseData) {
              if (!ex.completed || !ex.sets?.length) continue;
              const exerciseKey = ex.name.toLowerCase().trim();
              
              // Only set if we haven't found this exercise yet (most recent first)
              if (!previousWorkoutWeights.has(exerciseKey)) {
                // Get the max weight used in completed sets
                const maxWeight = Math.max(...ex.sets.map(s => s.weight || 0));
                const avgReps = Math.round(ex.sets.reduce((sum, s) => sum + (s.reps || 0), 0) / ex.sets.length);
                
                if (maxWeight > 0) {
                  previousWorkoutWeights.set(exerciseKey, { weight: maxWeight, reps: avgReps });
                  console.log(`[AIWeights] 📊 Found previous workout weight for "${ex.name}":`, maxWeight, 'lbs');
                }
              }
            }
          }
        }
        
        console.log('[AIWeights] Previous workout weights found:', previousWorkoutWeights.size);
        
        // Create exercise history from PRs and previous workouts using improved matching
        const historyMap = new Map<string, ExerciseHistory>();
        
        for (const exercise of exercises) {
          const exerciseNameLower = exercise.name.toLowerCase();
          
          // Use improved matching algorithm for PRs
          const matchingPR = findMatchingPR(exercise.name, typedPrs);
          
          if (matchingPR) {
            // Recommend 85% of PR for working sets
            const prWeight = matchingPR.weight;
            const recommendedWeight = Math.round((prWeight * 0.85) / 5) * 5;
            console.log(`[AIWeights] ✓ Matched "${exercise.name}" to PR "${matchingPR.lift}":`, prWeight, '→ recommended:', recommendedWeight);
            historyMap.set(exerciseNameLower, {
              exerciseName: exercise.name,
              lastWeight: prWeight,
              lastReps: parseInt(exercise.reps.split('-')[0]) || 10,
              recommendedWeight,
              isEstimate: false,
            });
          } else {
            // Check previous workout history for this exercise
            const previousWeight = previousWorkoutWeights.get(exerciseNameLower);
            
            if (previousWeight && previousWeight.weight > 0) {
              // Use previous workout weight as recommendation
              console.log(`[AIWeights] 💪 Using previous workout weight for "${exercise.name}":`, previousWeight.weight, 'lbs');
              historyMap.set(exerciseNameLower, {
                exerciseName: exercise.name,
                lastWeight: previousWeight.weight,
                lastReps: previousWeight.reps,
                recommendedWeight: previousWeight.weight, // Use same weight as last time
                isEstimate: false,
                isFromPreviousWorkout: true,
              });
            } else {
              // Try to get an AI estimate based on user profile
              const estimatedWeight = getEstimatedWeight(exercise.name, userProfile);
              
              if (estimatedWeight) {
                console.log(`[AIWeights] 🤖 AI estimate for "${exercise.name}":`, estimatedWeight, 'lbs');
                historyMap.set(exerciseNameLower, {
                  exerciseName: exercise.name,
                  lastWeight: 0,
                  lastReps: parseInt(exercise.reps.split('-')[0]) || 10,
                  recommendedWeight: estimatedWeight,
                  isEstimate: true,
                });
              } else if (isCommonPRExercise(exercise.name)) {
                console.log(`[AIWeights] ⚠ No PR or estimate for common exercise: "${exercise.name}"`);
                // Add a placeholder entry to indicate this is a trackable exercise
                historyMap.set(exerciseNameLower, {
                  exerciseName: exercise.name,
                  lastWeight: 0,
                  lastReps: parseInt(exercise.reps.split('-')[0]) || 10,
                  recommendedWeight: 0,
                  isEstimate: false,
                });
              } else {
                console.log(`[AIWeights] ✗ No PR or estimate for: "${exercise.name}"`);
              }
            }
          }
        }
        
        console.log('[AIWeights] Final history map entries:', historyMap.size);
        setExerciseHistory(historyMap);
      } catch (error) {
        console.error('[AIWeights] Error fetching recommendations:', error);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, [isOpen, exercises.length]);

  // Get recommendation for an exercise
  const getRecommendation = (exerciseName: string): ExerciseHistory | undefined => {
    return exerciseHistory.get(exerciseName.toLowerCase());
  };
  
  // Check if an exercise needs a PR prompt (common exercise without PR)
  const needsPRPrompt = (exerciseName: string): boolean => {
    const rec = exerciseHistory.get(exerciseName.toLowerCase());
    return rec !== undefined && rec.recommendedWeight === 0 && isCommonPRExercise(exerciseName);
  };

  // Apply AI recommendation to an exercise
  const applyRecommendation = (exerciseId: string, weight: number) => {
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      const updatedSets = ex.actualSets?.map(s => ({ ...s, weight })) || [];
      return { ...ex, actualSets: updatedSets };
    }));
    toast.success('Applied recommended weight!');
  };

  // NOTE: We no longer reset state when dialog closes - state persists in localStorage

  // Parse workout days when opened (only if no saved session exists)
  useEffect(() => {
    if (!isOpen) return;
    
    // Check if we have a saved session - if so, don't override
    const savedSession = localStorage.getItem(WORKOUT_SESSION_STORAGE_KEY);
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        if (parsed.exercises?.length > 0) {
          // Session was restored by the other useEffect, skip parsing
          return;
        }
      } catch (e) {
        // Continue with parsing
      }
    }
    
    if (planText) {
      const days = parseWorkoutPlanByDay(planText);
      setWorkoutDays(days);
      
      // If only one day, auto-select it
      if (days.length === 1) {
        setSelectedDayIndex(0);
        setExercises(days[0].exercises);
      } else if (days.length === 0) {
        // No days found, use default
        setSelectedDayIndex(0);
        setExercises(getDefaultWorkout());
      } else {
        // Multiple days - show picker
        setSelectedDayIndex(null);
        setExercises([]);
      }
    } else {
      setWorkoutDays([]);
      setSelectedDayIndex(0);
      setExercises(getDefaultWorkout());
    }
  }, [isOpen, planText]);

  // When a day is selected, load its exercises
  const handleSelectDay = (index: number) => {
    setSelectedDayIndex(index);
    setExercises(workoutDays[index].exercises);
  };

  // Timer effect that calculates elapsed time from start timestamp
  // This allows the timer to "catch up" when app returns from background
  useEffect(() => {
    if (!isOpen) return;

    // Function to update elapsed time based on start timestamp
    const updateElapsed = () => {
      const elapsed = Math.floor((Date.now() - workoutStartTime) / 1000);
      setElapsedSeconds(elapsed);
    };

    // Update immediately on mount/visibility change
    updateElapsed();

    // Regular interval update
    const interval = setInterval(updateElapsed, 1000);

    // Handle visibility change (app going to background/foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // App came back to foreground - recalculate elapsed time
        updateElapsed();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, workoutStartTime]);

  useEffect(() => {
    if (!isRestActive || restTimer === null) return;
    if (restTimer <= 0) {
      setIsRestActive(false);
      toast("Rest complete! 💪 Next set!");
      return;
    }
    const timeout = setTimeout(() => setRestTimer(prev => (prev || 0) - 1), 1000);
    return () => clearTimeout(timeout);
  }, [isRestActive, restTimer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress based on individual sets, not just completed exercises
  const totalSets = exercises.reduce((acc, ex) => acc + (ex.actualSets?.length || 0), 0);
  const completedSets = exercises.reduce((acc, ex) => 
    acc + (ex.actualSets?.filter(s => s.completed).length || 0), 0
  );
  const doneCount = exercises.filter(ex => ex.completed || ex.skipped).length;
  const progress = totalSets > 0 ? (completedSets / totalSets) * 100 : 0;


  const toggleSetComplete = (exerciseId: string, setNumber: number) => {
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      
      const updatedSets = ex.actualSets?.map(s => 
        s.setNumber === setNumber ? { ...s, completed: !s.completed } : s
      ) || [];
      
      const allComplete = updatedSets.every(s => s.completed);
      
      if (!ex.completed && allComplete) {
        const maxWeight = Math.max(...updatedSets.map(s => s.weight));
        const existingPR = existingPRs.find(pr => 
          pr.lift.toLowerCase() === ex.name.toLowerCase()
        );
        
        if (maxWeight > 0 && (!existingPR || maxWeight > existingPR.weight)) {
          toast.success(`🏆 NEW PR: ${ex.name} - ${maxWeight} lbs!`);
          onNewPR?.(ex.name, maxWeight);
        }
      }
      
      return { ...ex, actualSets: updatedSets, completed: allComplete };
    }));
    
    if (!isRestActive) {
      setRestTimer(90);
      setIsRestActive(true);
    }
  };

  const skipExercise = (exerciseId: string) => {
    setExercises(prev => prev.map(ex => 
      ex.id === exerciseId ? { ...ex, skipped: true, completed: false } : ex
    ));
    toast(`Skipped ${exercises.find(e => e.id === exerciseId)?.name}`);
  };

  const unskipExercise = (exerciseId: string) => {
    setExercises(prev => prev.map(ex => 
      ex.id === exerciseId ? { ...ex, skipped: false } : ex
    ));
  };

  const updateSetNotes = (exerciseId: string, setNumber: number, notes: string) => {
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      const updatedSets = ex.actualSets?.map(s => 
        s.setNumber === setNumber ? { ...s, notes } : s
      ) || [];
      return { ...ex, actualSets: updatedSets };
    }));
  };

  const toggleExerciseComplete = (exerciseId: string) => {
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      const newCompleted = !ex.completed;
      const updatedSets = ex.actualSets?.map(s => ({ ...s, completed: newCompleted })) || [];
      
      if (newCompleted) {
        const maxWeight = Math.max(...updatedSets.map(s => s.weight));
        const existingPR = existingPRs.find(pr => 
          pr.lift.toLowerCase() === ex.name.toLowerCase()
        );
        
        if (maxWeight > 0 && (!existingPR || maxWeight > existingPR.weight)) {
          toast.success(`🏆 NEW PR: ${ex.name} - ${maxWeight} lbs!`);
          onNewPR?.(ex.name, maxWeight);
        }
      }
      
      return { ...ex, actualSets: updatedSets, completed: newCompleted };
    }));
  };

  const deleteExercise = (exerciseId: string) => {
    setExercises(prev => prev.filter(ex => ex.id !== exerciseId));
    toast("Exercise removed");
  };

  const addSetToExercise = (exerciseId: string) => {
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      const newSetNumber = (ex.actualSets?.length || 0) + 1;
      const lastSet = ex.actualSets?.[ex.actualSets.length - 1];
      return {
        ...ex,
        sets: ex.sets + 1,
        actualSets: [
          ...(ex.actualSets || []),
          { 
            setNumber: newSetNumber, 
            reps: lastSet?.reps || 10, 
            weight: lastSet?.weight || 0, 
            completed: false 
          }
        ],
        completed: false,
      };
    }));
    toast("Set added");
  };

  const removeSetFromExercise = (exerciseId: string, setNumber: number) => {
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      if ((ex.actualSets?.length || 0) <= 1) return ex;
      
      const filteredSets = ex.actualSets?.filter(s => s.setNumber !== setNumber) || [];
      const renumberedSets = filteredSets.map((s, i) => ({ ...s, setNumber: i + 1 }));
      const allComplete = renumberedSets.every(s => s.completed);
      
      return {
        ...ex,
        sets: ex.sets - 1,
        actualSets: renumberedSets,
        completed: allComplete,
      };
    }));
  };

  const addNewExercise = () => {
    if (!newExerciseName.trim()) {
      toast.error("Enter an exercise name");
      return;
    }
    
    const newExercise: WorkoutExercise = {
      id: `ex-${Date.now()}`,
      name: newExerciseName.trim(),
      sets: newExerciseSets,
      reps: newExerciseReps.toString(),
      completed: false,
      skipped: false,
      actualSets: Array.from({ length: newExerciseSets }, (_, i) => ({
        setNumber: i + 1,
        reps: newExerciseReps,
        weight: 0,
        completed: false,
      })),
    };
    
    setExercises(prev => [...prev, newExercise]);
    setNewExerciseName('');
    setNewExerciseSets(3);
    setNewExerciseReps(10);
    setShowAddExercise(false);
    toast.success(`Added ${newExercise.name}`);
  };

  const updateSetWeight = (exerciseId: string, setNumber: number, weight: number) => {
    const safeWeight = Math.max(0, weight);
    
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      
      const updatedSets = ex.actualSets?.map(s => {
        // Update the current set and mark it as edited
        if (s.setNumber === setNumber) {
          return { ...s, weight: safeWeight, weightEdited: true };
        }
        // Auto-populate subsequent sets that haven't been manually edited
        if (s.setNumber > setNumber && !s.completed && !s.weightEdited) {
          return { ...s, weight: safeWeight };
        }
        return s;
      }) || [];
      
      return { ...ex, actualSets: updatedSets };
    }));
  };

  const updateSetReps = (exerciseId: string, setNumber: number, reps: number) => {
    const safeReps = Math.max(0, reps);
    
    setExercises(prev => prev.map(ex => {
      if (ex.id !== exerciseId) return ex;
      
      const updatedSets = ex.actualSets?.map(s => {
        // Update the current set and mark it as edited
        if (s.setNumber === setNumber) {
          return { ...s, reps: safeReps, repsEdited: true };
        }
        // Auto-populate subsequent sets that haven't been manually edited
        if (s.setNumber > setNumber && !s.completed && !s.repsEdited) {
          return { ...s, reps: safeReps };
        }
        return s;
      }) || [];
      
      return { ...ex, actualSets: updatedSets };
    }));
  };

  const handleFinish = async () => {
    if (isFinishing) return; // Prevent double-clicks
    
    setIsFinishing(true);
    const completedExercises = exercises.filter(ex => ex.completed && !ex.skipped);
    const session: WorkoutSessionType = {
      id: Date.now().toString(),
      date: new Date(),
      planName: 'Workout',
      exercises: exercises.filter(ex => !ex.skipped),
      startedAt: new Date(workoutStartTime),
      completedAt: new Date(),
      duration: Math.floor(elapsedSeconds / 60),
    };
    clearWorkoutSession(); // Clear localStorage
    
    try {
      // IMPORTANT: Wait for onComplete to finish before closing
      // This ensures workout data is saved AND chat message is sent
      await onComplete(session);
    } catch (error) {
      console.error('Error completing workout:', error);
      // Still close on error - data should be saved even if chat fails
    }
    
    onClose();
    toast.success(`Workout complete! ${completedExercises.length} exercises logged 🔥`);
    setIsFinishing(false);
  };

  // Helper to get recommended weight for an exercise
  const getRecommendedWeightForExercise = (exerciseName: string): { weight: number; source: string } | null => {
    // Check if we have a matching PR
    const matchingPR = findMatchingPR(exerciseName, existingPRs);
    if (matchingPR) {
      const recommendedWeight = Math.round(matchingPR.weight * 0.85);
      return { weight: recommendedWeight, source: `85% of ${matchingPR.weight}${weightUnit} PR` };
    }
    
    // Try AI estimate
    if (hasEstimatedWeights(exerciseName) && userProfileForEstimates.sex) {
      const estimated = getEstimatedWeight(exerciseName, userProfileForEstimates);
      if (estimated) {
        return { weight: estimated, source: 'AI estimate' };
      }
    }
    
    return null;
  };

  // Show day picker if multiple days and none selected
  if (workoutDays.length > 1 && selectedDayIndex === null) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-md bg-card p-0 max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/30">
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-primary" />
              Select Workout Day
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-5 space-y-3 overflow-y-auto flex-1">
            <p className="text-sm text-muted-foreground">
              Your plan has {workoutDays.length} workout days. Which one are you doing today?
            </p>
            
            <div className="space-y-3">
              {workoutDays.map((day, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-border/50 bg-secondary/30 overflow-hidden"
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-between h-auto py-3 px-4 rounded-none hover:bg-secondary/50"
                      onClick={() => handleSelectDay(index)}
                    >
                      <div className="text-left">
                        <p className="font-medium">{day.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {day.exercises.length} exercises
                        </p>
                      </div>
                      <Sparkles className="w-4 h-4 text-primary" />
                    </Button>
                  </div>
                ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[75vh] overflow-hidden flex flex-col bg-card p-0 top-[5%] translate-y-0 left-[50%] translate-x-[-50%]">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-border/30">
          <div className="flex items-center justify-between mb-3">
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {/* Back button when multiple days exist - preserves current day's data */}
              {workoutDays.length > 1 && selectedDayIndex !== null && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 -ml-2 mr-1"
                  onClick={() => {
                    // Save current exercises back to workoutDays before switching
                    setWorkoutDays(prev => prev.map((day, idx) => 
                      idx === selectedDayIndex ? { ...day, exercises } : day
                    ));
                    setSelectedDayIndex(null);
                  }}
                  title="Choose different workout"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <Dumbbell className="w-5 h-5 text-primary" />
              {workoutDays.length > 1 && selectedDayIndex !== null 
                ? workoutDays[selectedDayIndex].name 
                : 'Workout Session'}
            </DialogTitle>
            <div className="flex items-center gap-3 text-sm">
              <OneRMCalculator 
                weightUnit={weightUnit}
                trigger={
                  <button className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                    <Calculator className="w-3 h-3" />
                    1RM
                  </button>
                }
              />
              <div className="flex items-center gap-1">
                <Timer className="w-4 h-4 text-muted-foreground" />
                <span className="font-mono font-bold">{formatTime(elapsedSeconds)}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-1 mt-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{completedSets}/{totalSets} sets ({doneCount}/{exercises.length} exercises)</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full evolve-gradient transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Rest Timer Banner */}
        {isRestActive && restTimer !== null && (
          <div className="px-5 py-3 bg-primary/10 border-b border-primary/30 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Pause className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Rest Time</p>
                <p className="text-xs text-muted-foreground">Recover before next set</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold text-primary">{formatTime(restTimer)}</span>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setIsRestActive(false)}
              >
                Skip
              </Button>
            </div>
          </div>
        )}

        {/* Exercises List */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 space-y-3">
          {exercises.map((exercise, exerciseIndex) => (
            <div 
              key={exercise.id}
              className={cn(
                "rounded-xl border transition-all",
                exercise.skipped 
                  ? "bg-muted/30 border-border/20 opacity-60"
                  : exercise.completed 
                    ? "bg-primary/5 border-primary/30" 
                    : "bg-secondary/30 border-border/30"
              )}
            >
              {/* Exercise header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  {/* Clickable circle to complete entire exercise */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!exercise.skipped) {
                        toggleExerciseComplete(exercise.id);
                      }
                    }}
                    disabled={exercise.skipped}
                    className="hover:opacity-70 transition-opacity"
                  >
                    {exercise.skipped ? (
                      <SkipForward className="w-6 h-6 text-muted-foreground" />
                    ) : exercise.completed ? (
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    ) : (
                      <Circle className="w-6 h-6 text-muted-foreground hover:text-primary transition-colors" />
                    )}
                  </button>
                  <button
                    onClick={() => !exercise.skipped && setExpandedExercise(
                      expandedExercise === exercise.id ? null : exercise.id
                    )}
                    className="flex-1 text-left"
                    disabled={exercise.skipped}
                  >
                    <p className={cn(
                      "font-medium",
                      (exercise.completed || exercise.skipped) && "line-through text-muted-foreground"
                    )}>
                      {exercise.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {exercise.sets} sets × {exercise.reps} reps
                    </p>
                  </button>
                </div>
                
                <div className="flex items-center gap-1">
                  {exercise.skipped ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => unskipExercise(exercise.id)}
                      className="text-xs"
                    >
                      Undo
                    </Button>
                  ) : (
                    <>
                      {/* Expand/collapse toggle */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedExercise(expandedExercise === exercise.id ? null : exercise.id);
                        }}
                        title="Expand/collapse"
                      >
                        {expandedExercise === exercise.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </Button>
                      
                      {/* More options dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreVertical className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40" onCloseAutoFocus={(e) => e.preventDefault()}>
                          <DropdownMenuItem 
                            onClick={() => setSelectedExerciseForGif(exercise.name)}
                            className="gap-2"
                          >
                            <Info className="w-4 h-4" />
                            View Demo
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => skipExercise(exercise.id)}
                            className="gap-2"
                          >
                            <SkipForward className="w-4 h-4" />
                            Skip Exercise
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => deleteExercise(exercise.id)}
                            className="gap-2 text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded sets */}
              {expandedExercise === exercise.id && !exercise.skipped && (
                <div className="px-4 pb-4 space-y-3 border-t border-border/30 pt-3">
                  {/* AI Recommendation Banner - Compact */}
                  {(() => {
                    if (loadingRecommendations) {
                      return (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30 text-xs text-muted-foreground">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Loading recommendations...
                        </div>
                      );
                    }
                    
                    const rec = getRecommendation(exercise.name);
                    if (rec && rec.recommendedWeight > 0) {
                      const bgColor = rec.isEstimate 
                        ? "bg-blue-500/10 border-blue-500/20" 
                        : rec.isFromPreviousWorkout 
                          ? "bg-green-500/10 border-green-500/20"
                          : "bg-primary/10 border-primary/20";
                      const iconColor = rec.isEstimate ? "text-blue-500" : rec.isFromPreviousWorkout ? "text-green-500" : "text-primary";
                      const Icon = rec.isEstimate ? Sparkles : rec.isFromPreviousWorkout ? History : Trophy;
                      const label = rec.isEstimate 
                        ? `AI: ${rec.recommendedWeight} lbs` 
                        : rec.isFromPreviousWorkout 
                          ? `Last: ${rec.lastWeight} lbs` 
                          : `PR: ${rec.recommendedWeight} lbs`;
                      
                      return (
                        <button
                          onClick={() => applyRecommendation(exercise.id, rec.recommendedWeight)}
                          className={cn("flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors hover:opacity-80 w-full", bgColor)}
                        >
                          <Icon className={cn("w-3 h-3 shrink-0", iconColor)} />
                          <span className="font-medium">{label}</span>
                          <span className="text-muted-foreground ml-auto text-[10px]">Tap to apply</span>
                        </button>
                      );
                    }
                    
                    // PR tracking hint for common exercises - compact
                    if (needsPRPrompt(exercise.name)) {
                      return (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs">
                          <Trophy className="w-3 h-3 text-amber-500 shrink-0" />
                          <span>Add a {getDisplayNameForCommonExercise(exercise.name)} PR for suggestions</span>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}

                  {exercise.actualSets?.map((set) => (
                    <div 
                      key={set.setNumber}
                      className={cn(
                        "rounded-xl border transition-all",
                        set.completed 
                          ? "bg-primary/5 border-primary/30" 
                          : "bg-card border-border/40"
                      )}
                    >
                      {/* Compact set header with inline weight/reps */}
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            onClick={() => toggleSetComplete(exercise.id, set.setNumber)}
                            className="shrink-0 transition-transform hover:scale-110"
                          >
                            {set.completed ? (
                              <CheckCircle2 className="w-5 h-5 text-primary" />
                            ) : (
                              <Circle className="w-5 h-5 text-muted-foreground/60" />
                            )}
                          </button>
                          <span className="text-sm font-medium">Set {set.setNumber}</span>
                          
                          {/* Suggestion hint inline */}
                          {(() => {
                            const rec = getRecommendation(exercise.name);
                            if (rec && rec.recommendedWeight > 0 && set.weight !== rec.recommendedWeight) {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateSetWeight(exercise.id, set.setNumber, rec.recommendedWeight);
                                  }}
                                  className="text-[10px] text-primary hover:underline ml-auto flex items-center gap-0.5"
                                >
                                  <Sparkles className="w-2.5 h-2.5" />
                                  {rec.recommendedWeight}
                                </button>
                              );
                            }
                            return null;
                          })()}
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground/40 hover:text-destructive ml-auto"
                            onClick={() => removeSetFromExercise(exercise.id, set.setNumber)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {/* Inline weight and reps */}
                        <div className="flex gap-2">
                          {/* Weight */}
                          <div className="flex-1 flex items-center gap-1 bg-secondary/30 rounded-lg p-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSetWeight(exercise.id, set.setNumber, set.weight - 5);
                              }}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <div className="flex-1 text-center">
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={set.weight === 0 ? '' : set.weight}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const val = e.target.value.replace(/[^0-9]/g, '');
                                  const numVal = val === '' ? 0 : parseInt(val, 10);
                                  updateSetWeight(exercise.id, set.setNumber, numVal);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.target.select()}
                                className="h-8 w-full text-center text-lg font-bold bg-transparent border-0 p-0"
                                placeholder="0"
                              />
                              <span className="text-[10px] text-muted-foreground">lbs</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSetWeight(exercise.id, set.setNumber, set.weight + 5);
                              }}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          
                          {/* Reps */}
                          <div className="flex-1 flex items-center gap-1 bg-secondary/30 rounded-lg p-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSetReps(exercise.id, set.setNumber, set.reps - 1);
                              }}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <div className="flex-1 text-center">
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={set.reps === 0 ? '' : set.reps}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const val = e.target.value.replace(/[^0-9]/g, '');
                                  const numVal = val === '' ? 0 : parseInt(val, 10);
                                  updateSetReps(exercise.id, set.setNumber, numVal);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.target.select()}
                                className="h-8 w-full text-center text-lg font-bold bg-transparent border-0 p-0"
                              />
                              <span className="text-[10px] text-muted-foreground">reps</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSetReps(exercise.id, set.setNumber, set.reps + 1);
                              }}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Collapsible notes - only show if has notes or tapped */}
                        <div className="mt-2">
                          <Input
                            placeholder="📝 Note..."
                            value={set.notes || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateSetNotes(exercise.id, set.setNumber, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 text-xs bg-secondary/20 border-0 placeholder:text-muted-foreground/40"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full h-11 border-2 border-dashed border-border/40 hover:border-primary/50 hover:bg-primary/5 rounded-xl"
                    onClick={() => addSetToExercise(exercise.id)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Set
                  </Button>
                </div>
              )}
            </div>
          ))}
          
          {/* Add Exercise Section */}
          {showAddExercise ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
              <Input
                placeholder="Exercise name (e.g., Dumbbell Curls)"
                value={newExerciseName}
                onChange={(e) => setNewExerciseName(e.target.value)}
                className="bg-background"
              />
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Sets</label>
                  <div className="flex items-center gap-1 mt-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setNewExerciseSets(Math.max(1, newExerciseSets - 1))}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <Input
                      type="number"
                      value={newExerciseSets}
                      onChange={(e) => setNewExerciseSets(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-14 h-8 text-center"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setNewExerciseSets(newExerciseSets + 1)}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Reps</label>
                  <div className="flex items-center gap-1 mt-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setNewExerciseReps(Math.max(1, newExerciseReps - 1))}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <Input
                      type="number"
                      value={newExerciseReps}
                      onChange={(e) => setNewExerciseReps(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-14 h-8 text-center"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setNewExerciseReps(newExerciseReps + 1)}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowAddExercise(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={addNewExercise}
                >
                  Add Exercise
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              className="w-full border border-dashed border-border/50"
              onClick={() => setShowAddExercise(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Exercise
            </Button>
          )}
          </div>
        </div>

        {/* Footer - Compact */}
        <div className="px-4 py-3 border-t border-border/30 shrink-0">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 border border-border/30"
              onClick={onClose}
              title="Chat with Coach"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              className="h-10 px-4 border-destructive/50 text-destructive hover:bg-destructive/10"
              onClick={handleCancelWorkout}
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              className="flex-1 h-10 evolve-gradient"
              onClick={handleFinish}
              disabled={isFinishing}
            >
              {isFinishing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trophy className="w-4 h-4 mr-2" />
              )}
              {isFinishing ? 'Saving...' : 'Finish Workout'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    
    <ExerciseGifModal
      isOpen={!!selectedExerciseForGif}
      onClose={() => setSelectedExerciseForGif(null)}
      exerciseName={selectedExerciseForGif || ''}
    />
    
    <AlertDialog open={showCancelConfirmation} onOpenChange={setShowCancelConfirmation}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Cancel Workout?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will delete all your progress for this workout session. Your logged sets, weights, and reps will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Continue Workout</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmCancelWorkout}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Cancel Workout
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}